'use client';

import { useState } from 'react';
import type { FormEvent } from 'react';
import type { ValueOf } from 'type-fest';
import { useTranslations } from 'next-intl';
import { CloseButton, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import { HpApiWebApiError } from '@zg-rentals/ts-hotpads-api-client';
import type { HpUserCredentialsDto } from '@zg-rentals/ts-hotpads-api-client';
import { v3ErrorReason } from '@/src/lib/api/client/hotpadsApiV3Client';
import { useVerifyOtp } from '@/src/lib/hooks/query';
import { CODE_SPENT_REASONS, useOtpCodeEntry, useOtpResend } from '@/src/lib/hooks/auth';
import { OtpCodeEntry } from '@/src/components/features/auth/OtpCodeEntry/OtpCodeEntry';
import { OtpResendButton } from '@/src/components/features/auth/OtpCodeEntry/OtpResendButton';
import { GatedButton } from '@/src/components/ui/GatedButton';
import { maskEmail } from '@/src/lib/auth/email';
import { footerRow, header, stack } from './otpSignIn.styles';

export type OtpSignInFormProps = {
  /** Address the code is sent to. Owned by the caller — this form never edits it. */
  email: string;
  onBack?: () => void;
  onClose?: () => void;
  onVerified: (credentials: HpUserCredentialsDto) => void;
  /**
   * The caller's own send already hit the per-address budget. Starts the step in the same
   * state a rejected resend would: no further sends, but code entry stays open, because a
   * code already in the inbox is still redeemable.
   */
  sendLimitReached?: boolean;
  /**
   * Heading for the form. Omit on surfaces that supply their own chrome — the modal
   * renders it in `Modal.Header` so it sits where every other modal's title does.
   */
  title?: string;
};

const VERIFY_ERROR_COPY = {
  [HpApiWebApiError.INVALID_CREDENTIALS]: 'otpErrorInvalidCode',
  [HpApiWebApiError.CODE_EXPIRED]: 'otpErrorExpired',
  // Retires the code that was guessed at, not the address: the attempt tally is keyed on
  // the code hash and expires with it, so a new code grants a fresh set of attempts.
  [HpApiWebApiError.CODE_ATTEMPTS_EXCEEDED]: 'otpErrorTooManyAttempts',
  // sendOtp only: the per-address send budget is spent. A code already in the inbox stays
  // valid, so this must not block verifying.
  [HpApiWebApiError.SEND_LIMIT_EXCEEDED]: 'otpErrorSendLimit',
  // Generic throttle from the shared handler rather than the OTP flow itself.
  [HpApiWebApiError.TOO_MANY_REQUESTS]: 'otpErrorSendLimit',
  // Retrying never succeeds, so the generic "try again" copy would be actively wrong.
  [HpApiWebApiError.ACCOUNT_DEACTIVATED]: 'otpErrorAccountDeactivated',
  // Everything else falls back to the generic error on purpose. A dedicated message earns
  // its place only by naming a different next step: invalidEmail cannot be reached from here
  // (the address is validated before this step mounts), and the v3EndpointsEnabled kill
  // switch's 404 and a session signed in by another tab both amount to "try again later".
} as const satisfies Partial<Record<HpApiWebApiError, string>>;

type OtpMessageKey = ValueOf<typeof VERIFY_ERROR_COPY> | 'errorGeneric' | 'otpErrorSendFailed';

/** Resolves a v3 reason to its copy key, falling back when the reason has none of its own. */
const messageKeyFor = (reason: HpApiWebApiError | undefined, fallback: OtpMessageKey): OtpMessageKey =>
  reason && reason in VERIFY_ERROR_COPY ? VERIFY_ERROR_COPY[reason as keyof typeof VERIFY_ERROR_COPY] : fallback;

/**
 * Email one-time-code sign-in.
 *
 * Collects the code, verifies it, and resends on request; on success the session is live
 * and the current-user cache already reflects it, so the caller only has to decide what
 * happens next. The caller sends the first code — see useOtpResend for why this cannot do
 * it. Renders no chrome of its own beyond an optional title, and never offers social or
 * password sign-in.
 */
export function OtpSignInForm({ email, onBack, onClose, onVerified, sendLimitReached, title }: OtpSignInFormProps) {
  const t = useTranslations('auth');
  const verifyOtp = useVerifyOtp();

  const [errorMessage, setErrorMessage] = useState(sendLimitReached ? t('otpErrorSendLimit') : '');

  const entry = useOtpCodeEntry({ onComplete: (completed) => submitCode(completed) });
  const resend = useOtpResend({
    email,
    sendLimitReached,
    onResent: entry.resetForNewCode,
    onSendError: (reason) => setErrorMessage(t(messageKeyFor(reason, 'otpErrorSendFailed'))),
  });

  const requestNewCode = () => {
    setErrorMessage('');
    resend.resend();
  };

  const submitCode = (codeToVerify: string) => {
    if (verifyOtp.isPending || !entry.beginAttempt(codeToVerify)) {
      return;
    }
    setErrorMessage('');
    resend.clearConfirmation();

    verifyOtp.mutate(
      { postData: { code: codeToVerify, email } },
      {
        onSuccess: (response) => {
          if (!response.data) {
            setErrorMessage(t('otpErrorSendFailed'));
            return;
          }
          onVerified(response.data);
        },
        onError: (error) => {
          const reason = v3ErrorReason(error);
          setErrorMessage(t(messageKeyFor(reason, 'errorGeneric')));
          if (reason && CODE_SPENT_REASONS.has(reason)) {
            entry.lockEntry();
            return;
          }
          entry.rejectCode();
        },
      },
    );
  };

  const handleCodeChange = (next: string) => {
    if (errorMessage && !entry.entryLocked) {
      setErrorMessage('');
    }
    entry.handleCodeChange(next);
  };

  const isVerifying = verifyOtp.isPending;
  const canSubmit = entry.isComplete && !isVerifying && !entry.entryLocked;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (canSubmit) {
      submitCode(entry.code);
    }
  };

  const gateReason = () => {
    if (isVerifying) return undefined;
    if (entry.entryLocked) return errorMessage;
    if (!entry.isComplete) return t('otpGateHintCode');
    return undefined;
  };

  return (
    <div className={stack}>
      {(title || onClose) && (
        <div className={header}>
          {title && (
            <Heading level={2} textStyle="heading-xs">
              {title}
            </Heading>
          )}
          {onClose && <CloseButton type="button" title={t('otpClose')} onClick={onClose} />}
        </div>
      )}

      <Paragraph>{t('otpDescription', { email: maskEmail(email) })}</Paragraph>

      <form className={stack} onSubmit={handleSubmit} noValidate>
        <OtpCodeEntry
          code={entry.code}
          onCodeChange={handleCodeChange}
          disabled={isVerifying || entry.entryLocked}
          isVerifying={isVerifying}
          errorMessage={errorMessage}
          resendConfirmed={resend.resendConfirmed}
        />

        <GatedButton
          type="submit"
          tone="brand"
          emphasis="filled"
          size="lg"
          fluid
          disabled={!canSubmit}
          loading={isVerifying}
          gateReason={gateReason()}
        >
          {t('otpContinue')}
        </GatedButton>
      </form>

      <OtpResendButton
        onResend={requestNewCode}
        isSending={resend.isSending}
        cooldownSeconds={resend.cooldownSeconds}
        disabled={resend.resendDisabled}
      />

      {onBack && (
        <div className={footerRow}>
          <TextButton type="button" onClick={onBack}>
            {t('otpBack')}
          </TextButton>
        </div>
      )}
    </div>
  );
}
